/* eslint-disable max-len */
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { AvailabilityType, NonJobEventOptions } from './non-job-event.types';
import { ZuperDrawerService } from '@zuper/components/zuper-drawer';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import moment, { Moment } from 'moment';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';
import { PreviewAddressFormComponent } from '../preview-address-form/preview-address-form.component';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { DispatchBoardService } from '../../../../projects/dispatch-board/src/app/components/dispatch-board/dispatch-board.service';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { NewCategoryComponent } from '../new-category/new-category.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { HotToastService } from '@ngneat/hot-toast';
import { EditorOptions } from '../text-editor/text-editor.interface';
import _ from 'lodash';

@Component({
  selector: 'create-non-job-event',
  templateUrl: './create-non-job-event.component.html',
  styleUrls: ['./create-non-job-event.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CreateNonJobEventComponent implements OnInit, OnChanges {

  @Input() drawerName: string = 'createNonJobEventForm';
  @Input() openDrawer: boolean;
  @Input() options: NonJobEventOptions;
  @Input() eventData: any;
  @Input() timezone?: string = this.utilityService.timeZone;
  @Output() actionEmitter: EventEmitter<any> = new EventEmitter();

  @ViewChild('eventLocationElementRef') eventLocationElementRef: ElementRef;
  @ViewChild('categorySelectorRef') categorySelectorRef: NgSelectComponent;

  //General
  timezoneDisplayName: string;
  nonJobEventForm: FormGroup;
  dateFormat: string;
  timeFormat: string;
  dateTimeFormat: string;
  isMeridian: boolean;
  selectedTimezone: string;
  businessConfig = { startTime: null, endTime: null };
  cancelAllSubscription: Subject<void> = new Subject<void>();
  tooltipContent = { availability: '', notifyAttendees: '' };
  enableMultipleTimezoneSwitching: boolean = false;
  userRole: string;
  isLoading: boolean = false;
  eventStartEndTimeText: string;
  textEditorOptions: EditorOptions = {
    editorId:'categoryDescription',
    attachmentType: 'JOB_NOTE',
    module:'JOB',
    isForm: true,
    showTemplate: false
  };

  //Employees
  $employees: Observable<any[]> = new Observable<any[]>();
  employeeValueSubject: ReplaySubject<string | null> = new ReplaySubject<string | null>();
  employeeFetching: boolean = false;
  employeeOptions: any[] = [];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  employeeParamObj: any = { 'count': 10, 'page': 1, 'filter.is_active': true };
  employeePagination: any = {
    loadingIndex: 1,
    totalPage: 1,
    keyword: ''
  };
  $categories: Observable<any[]> = new Observable<any[]>();
  categoryParamObj: any = { 'count': 10, 'page': 1 };
  categoryFetching: boolean = false;
  categoryOptions: any[] = [];

  //Schedule
  $timezones: Observable<any[]> = new Observable<any[]>();
  recurrenceData = { options: { custom: false, state: 'new', module: 'Non job event'} };

  constructor(
    private utilityService: UtilityService,
    private zuperDrawerService: ZuperDrawerService,
    private localisationService: LocalisationService,
    private formBuilder: FormBuilder,
    private dataCollectorService: DataCollectorService,
    private zuperDialogService: ZuperDialogService,
    private dispatchBoardService: DispatchBoardService,
    private localStorageService: LocalStorageService,
    private toastService: HotToastService
  ) { }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    this.dateFormat = this.utilityService.dateFormat;
    this.timeFormat = this.utilityService.timeFormat;
    this.dateTimeFormat = this.utilityService.dateTimeFormat;
    this.isMeridian = this.timeFormat === 'hh:mm A';
    this.userRole = this.localStorageService.getLocalStorage('user')?.role.role_key || '';
    this.businessConfig.startTime = this.dispatchBoardService.getBusinessHours('start');
    this.businessConfig.endTime = this.dispatchBoardService.getBusinessHours('end');
    this.enableMultipleTimezoneSwitching = this.utilityService.companyConfig?.general?.allow_switching_timezones ?? false;
    this.tooltipContent.availability = this.utilityService.getLocaleValue('non_job_event.create.availability.tooltip', 'The scheduled slot will be blocked in Assisted Scheduling for the attendees when the availability is set to busy.');
    this.tooltipContent.notifyAttendees = this.utilityService.getLocaleValue('non_job_event.create.notify_attendees.tooltip', 'The attendees will be notified via push message on mobile and desktop');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('openDrawer' in changes) {
      this.openDrawer = changes?.openDrawer?.currentValue;
      const drawerRef = this.zuperDrawerService.getComponent(this.drawerName);
      if (drawerRef) {
        if (this.openDrawer) {
          drawerRef.open();
        } else {
          drawerRef.close();
        }
      }
    }

    if ('timezone' in changes) {
      if (this.timezone) {
        this.timezoneDisplayName = this.utilityService.getTimezoneDisplayName(this.timezone);
        if (this.nonJobEventForm) {
          this.nonJobEventForm.get('schedule.timezone').setValue(this.timezone);
        }
      }
    }
  }

  //General
  initDependencies(): void {
    this.selectedTimezone = this.enableMultipleTimezoneSwitching ? this.eventData?.timezone || this.timezone || this.utilityService.timeZone : this.utilityService.timeZone;
    moment.tz.setDefault(this.selectedTimezone);
    this.initNonJobEventForm();
    this.$employees = this.getEmployees('');
    this.$categories = this.getCategories();
    this.$timezones = this.getTimezone();
  }

  initNonJobEventForm(): void {
    const eventFromDate: Moment = this.eventData?.start_time ? moment(this.eventData?.start_time).tz(this.selectedTimezone) : this.utilityService.getNextQuarterHour(moment(), true);
    const eventToDate: Moment = this.eventData?.end_time ? moment(this.eventData?.end_time).tz(this.selectedTimezone) : eventFromDate.clone().add(1, 'h');
    let assignedTo: any[] = this.eventData?.assigned_to || [];
    assignedTo = assignedTo.map((userDetails: any) => ({...userDetails?.user, team: userDetails?.team || null, hasValidProfilePicture : this.utilityService.hasValidProfilePicture(userDetails?.user?.profile_picture)}));
    const prefillRecurrence: boolean = !!this.eventData?.recurrence?.rrule && this.options.state === 'edit';
    this.nonJobEventForm = this.formBuilder.group({
      eventName: [this.eventData?.event_name ?? null, [Validators.required, Validators.maxLength(200)]],
      category: [this.eventData?.event_category?.category_uid, Validators.required],
      description: [this.eventData?.event_description || '', maxPlainTextLengthValidator(8000)],
      assignedTo: [assignedTo || []],
      eventAddress: this.getAddressFormGroup(this.eventData?.event_address),
      eventAddressText: [this.dispatchBoardService.getAddressText(this.eventData?.event_address) || ''],
      schedule: this.formBuilder.group({
        allDay: [this.eventData?.all_day ?? false],
        timezone: [this.selectedTimezone, Validators.required],
        eventDate: [eventFromDate, Validators.required],
        fromTime: [eventFromDate?.format(this.timeFormat), Validators.required],
        fromTimePicker: [eventFromDate],
        toTime: [eventToDate?.format(this.timeFormat), Validators.required],
        toTimePicker: [eventToDate],
        eventDateTimeText: ['']
      }),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      date_time: [eventFromDate],
      availability: [this.eventData?.is_available ? AvailabilityType.FREE : AvailabilityType.BUSY],
      notifyAttendees: [true],
      recurrence: [prefillRecurrence ? 'CUSTOM' : ''],
      // eslint-disable-next-line @typescript-eslint/naming-convention
      recurrence_ends: this.formBuilder.group({
        type: ['recurrence_ends_on', [Validators.required]],
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ends_on: [prefillRecurrence ? moment(this.eventData?.end_time) : eventToDate?.clone().add(7, 'day')],
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ends_after: [1, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/), Validators.min(1), Validators.max(1000)]],
      }),
      repeat: this.formBuilder.group({
        count: [prefillRecurrence ? this.eventData?.recurrence?.duration?.value || 1 : 1, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/), Validators.min(1), Validators.max(100)]],
        type: [prefillRecurrence ? this.eventData?.recurrence?.duration?.type || 'WEEKS' : 'WEEKS', [Validators.required]],
      }),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      RRule: [prefillRecurrence ? this.eventData?.recurrence?.rrule : ''],
    });
    this.updateEventdateTimeForRecurrence();

    if (this.nonJobEventForm.get('assignedTo').value?.length) {
      this.onEmployeeChange();
    }

    if (this.options.state === 'edit' && prefillRecurrence) {
      this.recurrenceData.options.state = 'edit';
    }
    else {
      this.recurrenceData.options.state = 'new';
    }

    this.nonJobEventForm.get('schedule.allDay').valueChanges.pipe(takeUntil(this.cancelAllSubscription)).subscribe((value: any) => {
      if (value) {
        this.nonJobEventForm.get('schedule.fromTimePicker').setValue(moment(this.businessConfig.startTime, 'HH:mm'));
        this.nonJobEventForm.get('schedule.toTimePicker').setValue(moment(this.businessConfig.endTime, 'HH:mm'));
        this.updateEventStartEndTimeText();
      }
    });

    this.nonJobEventForm.get('schedule.timezone').valueChanges.pipe(takeUntil(this.cancelAllSubscription)).subscribe((value: string) => {
      this.selectedTimezone = value;
      this.timezoneDisplayName = this.utilityService.getTimezoneDisplayName(value);
      this.updateEventStartEndTimeText();
      this.updateEventdateTimeForRecurrence();
    });

    this.nonJobEventForm.get('schedule.fromTimePicker').valueChanges.pipe(takeUntil(this.cancelAllSubscription)).subscribe((value: Moment) => {
      this.nonJobEventForm.get('schedule.fromTime').setValue(value.format(this.timeFormat));
      this.checkStartEndTimeValidator();
      this.updateEventdateTimeForRecurrence();
    });

    this.nonJobEventForm.get('schedule.toTimePicker').valueChanges.pipe(takeUntil(this.cancelAllSubscription)).subscribe((value: Moment) => {
      this.nonJobEventForm.get('schedule.toTime').setValue(value.format(this.timeFormat));
      this.checkStartEndTimeValidator();
      this.updateEventdateTimeForRecurrence();
    });

    this.nonJobEventForm.get('schedule.eventDate').valueChanges.pipe(takeUntil(this.cancelAllSubscription)).subscribe((value: Moment) => {
      this.updateEventdateTimeForRecurrence();
      this.updateEventStartEndTimeText();
    });

    this.nonJobEventForm.get('category').valueChanges.pipe(takeUntil(this.cancelAllSubscription)).subscribe((value: string) => {
      const categoryDetails: any = this.categoryOptions.find((category: any) => category?.category_uid === value);
      if (categoryDetails) {
        this.nonJobEventForm.get('description').setValue(categoryDetails?.category_description || '');
        this.nonJobEventForm.get('availability').setValue(categoryDetails?.is_available ? AvailabilityType.FREE : AvailabilityType.BUSY);
      }
    });
  }

  tearDown(): void {
    this.cancelAllSubscription.next();
    this.nonJobEventForm.reset();
  }

  updateEventStartEndTimeText(): void {
    const timezone = this.nonJobEventForm.get('schedule.timezone').value || this.selectedTimezone;
    const timezoneCode: string =  moment().tz(timezone).format('Z');
    const eventStartTime: Moment = moment(this.nonJobEventForm.get('schedule.eventDate').value?.clone().format('YYYY-MM-DD') + ' ' + this.nonJobEventForm.get('schedule.fromTimePicker').value?.clone().format('HH:mm'), 'YYYY-MM-DD HH:mm');
    const eventEndTime: Moment = moment(this.nonJobEventForm.get('schedule.eventDate').value?.clone().format('YYYY-MM-DD') + ' ' + this.nonJobEventForm.get('schedule.toTimePicker').value?.clone().format('HH:mm'), 'YYYY-MM-DD HH:mm');
    const startEndTime: any[] = this.dispatchBoardService.formatStartEndTime(eventStartTime, eventEndTime);
    this.eventStartEndTimeText = `${this.utilityService.getLocaleValue('non_job_event.create.event_scheduled_text', 'The event is scheduled from')} ${startEndTime[0]} to ${startEndTime[1]} (GMT ${timezoneCode})`;
  }

  updateEventdateTimeForRecurrence(): void {
    const eventStartTime: any = moment(this.nonJobEventForm.get('schedule.eventDate').value?.clone().format('YYYY-MM-DD') + ' ' + this.nonJobEventForm.get('schedule.fromTimePicker').value?.clone().format('HH:mm') +':00', 'YYYY-MM-DD HH:mm:ss').utc().toISOString();
    const eventEndTime: any = moment(moment(this.nonJobEventForm.get('recurrence_ends.ends_on').value)?.clone().format('YYYY-MM-DD') + ' ' + this.nonJobEventForm.get('schedule.toTimePicker').value?.clone().format('HH:mm') +':00', 'YYYY-MM-DD HH:mm:ss').utc().toISOString();
    this.nonJobEventForm.get('date_time').setValue(eventStartTime, { emitEvent: false });
    this.nonJobEventForm.get('recurrence_ends.ends_on').setValue(eventEndTime);
  }

  getAddressFormGroup(address?: any): FormGroup {
    if (address?.geo_cordinates?.length !== 2) {
      address = null;
    }
    return this.formBuilder.group({
      'street': [address?.street || ''],
      'landmark': [address?.landmark || ''],
      'city': [address?.city || ''],
      'state': [address?.state || ''],
      'latitude': [address?.geo_cordinates?.[0] || ''],
      'longitude': [address?.geo_cordinates?.[1] || ''],
      'country': [address?.country || ''],
      'zipcode': [address?.zip_code || '']
    });
  }

  createNewCategory(): void {
    this.nonJobEventForm.get('category').clearValidators();
    this.nonJobEventForm.get('category').updateValueAndValidity();
    const categoryDialogRef: MatDialogRef<NewCategoryComponent> = this.zuperDialogService.openCustom(NewCategoryComponent,
      {
        extraClass: 'new-category-form',
        data: { moduleName: 'NON_JOB_EVENT', customColors: ['#69A3DD', '#07B0B0', '#FFCC00', '#FF6F61', '#91A1B1', '#9EA62B', '#7E6FDC', '#E61942', '#97BF9B'] }
      });
    categoryDialogRef.afterClosed().subscribe((dialogData: any) => {
      if (dialogData?.category?.category_uid) {
        this.$categories = this.getCategories(dialogData?.category?.category_uid);
      }
      else {
        this.nonJobEventForm.get('category').setValidators(Validators.required);
        this.nonJobEventForm.get('category').updateValueAndValidity();
      }
      if (this.categorySelectorRef) {
        this.categorySelectorRef.blur();
      }
    });
  }

  //Employees
  onEmployeeClear(): void {
    this.$employees = this.getEmployees('');
  }

  getEmployees(input: string): Observable<any[]> {
    this.employeePagination.keyword = input ? input : '';
    const url = `${this.dataCollectorService.baseurl()}/user/all`;
    this.employeeParamObj['filter.keyword'] = this.employeePagination.keyword;
    this.employeeParamObj['page'] = 1;
    const urlParams = this.utilityService.constructParam(this.employeeParamObj);
    this.employeeFetching = true;
    return this.dataCollectorService.getData(url, urlParams)
      .pipe(
        catchError(() => of([])),
        map((response: any) => {
          this.employeePagination.loadingIndex = response.current_page;
          this.employeePagination.totalPage = response.total_pages;
          if (response?.data) {
            this.employeeOptions = response.data?.map((emp: any) => {
              if (emp?.profile_picture) {
                emp['hasValidProfilePicture'] = this.utilityService.hasValidProfilePicture(emp?.profile_picture);
              }
              return emp;
            });
          }
          this.employeeFetching = false;
          return response?.data || [];
        }),
      );
  }

  onEmployeeSearch(): void {
    this.$employees = this.employeeValueSubject.pipe(
      distinctUntilChanged(),
      debounceTime(250),
      filter(value => (value !== null && value !== undefined)),
      switchMap(term => this.getEmployees(term))
    );
  }

  onEmployeeChange(): void {
    const assignedTo: any[] = this.nonJobEventForm?.controls?.assignedTo?.value || [];
    if (assignedTo?.length) {
      assignedTo.forEach(async (userDetails: any) => {
        if (userDetails?.profile_picture) {
          userDetails['hasValidProfilePicture'] = this.utilityService.hasValidProfilePicture(userDetails?.profile_picture);
        }
        if (!userDetails?.work_hours) {
          const userUid = userDetails?.user_uid;
          const userWorkHours = await this.dispatchBoardService.getUserWorkHours(userUid);
          userDetails['work_hours'] = userWorkHours;
        }
      });
      this.nonJobEventForm?.controls?.assignedTo?.setValue(assignedTo);
    }
  }

  onEmployeeScrollToEnd(): void {
    if (this.employeePagination.loadingIndex < this.employeePagination.totalPage) {
      const url = `${this.dataCollectorService.baseurl()}/user/all`;
      this.employeePagination.loadingIndex++;
      this.employeeParamObj['filter.keyword'] = this.employeePagination.keyword;
      this.employeeParamObj['page'] = this.employeePagination.loadingIndex;
      this.employeeFetching = true;
      const urlParams = this.utilityService.constructParam(this.employeeParamObj);
      this.dataCollectorService.getData(url, urlParams).subscribe(
        (response: any) => {
          this.employeeFetching = false;
          this.employeeOptions = this.employeeOptions.concat(response.data);
          this.employeeOptions = this.employeeOptions.map((emp) => {
            if (emp?.profile_picture) {
              emp['hasValidProfilePicture'] = this.utilityService.hasValidProfilePicture(emp?.profile_picture);
            }
            return emp;
          });
          this.$employees = this.nonJobEventForm?.controls?.assignedTo?.valueChanges?.pipe(
            startWith(''),
            map((value: any) => this.employeeOptions)
          );
        }, (error: any) => {
          this.employeeFetching = false;
        });
    }
  }

  isEmployeeAssigned(item: any): boolean {
    if (this.nonJobEventForm?.controls?.assignedTo.value?.length) {
      const selectedUser: any = this.nonJobEventForm?.controls?.assignedTo.value?.find(value => item?.user_uid === value?.user_uid);
      return selectedUser ? true : false;
    }
    return false;
  }

  userSelectionChanged(event: any, item: any): void {
    event.stopPropagation();
    if (!event?.target?.checked && this.nonJobEventForm?.controls?.assignedTo.value?.find(value => item?.user_uid === value?.user_uid)) {
      this.unassignEmployee(item);
    }
  }

  unassignEmployee(item: any): void {
    if (this.nonJobEventForm?.controls?.assignedTo?.value?.length) {
      const selectedUsers: any[] = this.nonJobEventForm?.controls?.assignedTo?.value?.filter(value => item?.user_uid !== value?.user_uid);
      this.nonJobEventForm?.controls?.assignedTo?.setValue(selectedUsers);
    }
  }

  //Categories
  getCategories(selectCategory?: string): Observable<any[]> {
    const url = `${this.dataCollectorService.baseurl()}/non_job_events/category`;
    const payload: any = {
      'sort': 'ASC',
      'sort_by': 'sequence'
    };

    this.categoryFetching = true;
    return this.dataCollectorService.getData(url, payload)
      .pipe(
        tap((response: any) => {
          if (selectCategory) {
            this.nonJobEventForm.get('category').setValue(selectCategory);
            this.nonJobEventForm.get('category').setValidators(Validators.required);
            this.nonJobEventForm.get('category').updateValueAndValidity();
          }
        }),
        catchError(() => of([])),
        map((response: any) => {
          this.categoryOptions = response?.data || [];
          const categoryDetails: any = this.categoryOptions.find((category: any) => category?.category_uid === selectCategory);
          if (selectCategory) {
            this.nonJobEventForm.get('description').setValue(categoryDetails?.category_description || '');
            this.nonJobEventForm.get('availability').setValue(categoryDetails?.is_available ? AvailabilityType.FREE : AvailabilityType.BUSY);
          }
          this.categoryFetching = false;
          return response.data;
        }),
      );
  }

  //Schedule
  getTimezone(): Observable<any[]> {
    if (this.dispatchBoardService.allTimezoneDetails?.length) {
      const timezones = this.dispatchBoardService.allTimezoneDetails.map((timezone: any) => ({ ...timezone, name: `${timezone?.display_name || ''}` }));
      return of(timezones);
    }
    else {
      return this.dataCollectorService.getData(this.dispatchBoardService.timezoneUrl)
        .pipe(catchError(() => of([])), map((response) => {
          let timezones = response?.data || [];
          timezones = timezones.map((timezone: any) => ({ ...timezone, name: `${timezone?.display_name || ''}` }));
          return timezones;
        }));
    }
  }

  openMapLocationSelectorDialog(): void {
    const formControlName: string = 'eventAddress';
    const previewAddress: MatDialogRef<PreviewAddressFormComponent> = this.zuperDialogService.openCustom(PreviewAddressFormComponent,
      {
        extraClass: 'create-route-preview-address-form',
        data: {
          header: {
            titleDisplayKey: 'non_job_event.create.event_address',
            titleDisplayName: 'Event Address'
          },
          formGroupName: formControlName,
          formGroup: this.nonJobEventForm,
          value: this.nonJobEventForm?.controls[formControlName]?.value,
          module: 'createRoute',
          optionalAddress: true
        }
      });
    previewAddress.disableClose = true;
    previewAddress.afterClosed().subscribe((result: any) => {
      if (!result?.city) {
        result['city'] = '';
      }
      if (result) {
        this.nonJobEventForm?.get(formControlName)?.setValue(result);
        this.nonJobEventForm?.get('eventAddressText')?.setValue(this.dispatchBoardService.getAddressText(result));
      }
      const eventLocationElementRef: HTMLInputElement = this.eventLocationElementRef.nativeElement;
      if (eventLocationElementRef) {
        eventLocationElementRef.blur();
      }
    });
  }

  clearEventAddress(): void {
    this.nonJobEventForm?.get('eventAddress')?.reset();
    this.nonJobEventForm?.get('eventAddressText')?.reset();
  }

  checkStartEndTimeValidator(): void {
    let startTime: Moment = this.nonJobEventForm.get('schedule.fromTimePicker')?.value?.clone();
    let endTime: Moment = this.nonJobEventForm.get('schedule.toTimePicker')?.value?.clone();
    startTime = moment(startTime.format('HH:mm'), 'HH:mm');
    endTime = moment(endTime.format('HH:mm'), 'HH:mm');
    if (startTime.isSameOrAfter(endTime)) {
      this.nonJobEventForm.get('schedule.fromTime').setErrors({ invalid: true });
      this.nonJobEventForm.get('schedule.toTime').setErrors({ invalid: true });
    }
    else {
      this.nonJobEventForm.get('schedule.fromTime').setErrors(null);
      this.nonJobEventForm.get('schedule.toTime').setErrors(null);
    }
    this.nonJobEventForm.get('schedule.fromTime').markAsTouched();
    this.nonJobEventForm.get('schedule.toTime').markAsTouched();
  }

  emitAction(event: any): void {
    this.actionEmitter.emit(event);
  }

  scrollToInvalidField(): void {
    const formElement: any = document.getElementById('non-job-event-form');
    if (formElement) {
      const invalidFormElements = formElement.getElementsByClassName('ng-invalid');
      if (invalidFormElements?.length) {
        const firstInvalidControl = invalidFormElements[0];
        firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        firstInvalidControl.focus();
      }
    }
}

  async onSubmitForm(): Promise<void> {
    this.nonJobEventForm.markAllAsTouched();
    if (this.nonJobEventForm.invalid) {
      this.scrollToInvalidField();
    }
    else {
      /* eslint-disable @typescript-eslint/naming-convention */
      const nonJobEventFormValue: any = this.nonJobEventForm?.value;
      const timezone: string = nonJobEventFormValue.schedule?.timezone;
      const eventStartTime: string = moment.tz(nonJobEventFormValue?.schedule?.eventDate?.clone().format('YYYY-MM-DD') + ' ' + nonJobEventFormValue?.schedule?.fromTimePicker?.clone().format('HH:mm'), timezone).utc().format('YYYY-MM-DD HH:mm');
      const eventEndTime: string = moment.tz(nonJobEventFormValue?.schedule?.eventDate?.clone().format('YYYY-MM-DD') + ' ' +  nonJobEventFormValue?.schedule?.toTimePicker?.clone().format('HH:mm'), timezone).utc().format('YYYY-MM-DD HH:mm');
      const assignedTo: any[] = nonJobEventFormValue?.assignedTo.map((userDetails: any) => ({user_uid: userDetails?.user_uid, team_uid: userDetails?.teams?.[0]?.team_uid || userDetails?.team?.team_uid || null})) || [];
      const payload = {
        event: {
          event_name: nonJobEventFormValue?.eventName || '',
          event_category: nonJobEventFormValue?.category || '',
          event_description: nonJobEventFormValue?.description || '',
          assigned_to: assignedTo,
          all_day: nonJobEventFormValue.schedule?.allDay,
          timezone: timezone,
          start_time: eventStartTime,
          end_time: eventEndTime,
          is_available: nonJobEventFormValue.availability === AvailabilityType.FREE,
          notify_users: nonJobEventFormValue.notifyAttendees
        }
      };
      const hasValidGeoCords: boolean = this.utilityService.isValidGeoCords([nonJobEventFormValue.eventAddress?.latitude, nonJobEventFormValue.eventAddress?.longitude]);
      if (hasValidGeoCords || this.options.state === 'edit') {
        payload.event['event_address'] = {
          ...nonJobEventFormValue.eventAddress,
          'street': nonJobEventFormValue.eventAddress?.street || '',
          'country': nonJobEventFormValue.eventAddress?.country?.label || nonJobEventFormValue.eventAddress?.country || '',
          'zip_code': nonJobEventFormValue.eventAddress?.zipcode || '',
          'geo_cordinates': hasValidGeoCords && nonJobEventFormValue.eventAddress?.latitude + '' && nonJobEventFormValue.eventAddress?.longitude + '' ? [nonJobEventFormValue.eventAddress?.latitude, nonJobEventFormValue.eventAddress?.longitude] : undefined
        };
      }
      if (this.nonJobEventForm.controls.recurrence?.value && !(this.options.state === 'edit' && this.options.editType === 'SINGLE_EVENT')) {
        payload.event['is_recurrence'] = true;
        payload.event['recurrence'] = {
          'rule_string': nonJobEventFormValue?.RRule?.toString().replace(':', '=').replace('\n', ';').replace('RRULE:', ''),
          'duration': {
            'value': nonJobEventFormValue?.repeat?.count || 1,
            'type': nonJobEventFormValue?.repeat?.type
          }
        };
        if (nonJobEventFormValue?.RRule && !(this.options.state === 'edit' && this.options.editType === 'SINGLE_EVENT')) {
          const allRecurrenceDates: any[] = nonJobEventFormValue?.RRule?.all() || [];
          const recurrenceDates: any[] = [];
          if (allRecurrenceDates?.length) {
            allRecurrenceDates.forEach((date: any) => {
              const startDateTime: string = moment.tz(moment(date).format('YYYY-MM-DD') + ' ' + nonJobEventFormValue?.schedule?.fromTimePicker?.clone().format('HH:mm'), timezone).utc().format('YYYY-MM-DD HH:mm');
              const endDateTime: string = moment.tz(moment(date).format('YYYY-MM-DD') + ' ' + nonJobEventFormValue?.schedule?.toTimePicker?.clone().format('HH:mm'), timezone).utc().format('YYYY-MM-DD HH:mm');
              recurrenceDates.push({ start_time: startDateTime, end_time: endDateTime });
            });
          }
          payload.event['recurrence_dates'] = recurrenceDates?.length ? recurrenceDates : [];
        }
      }

      let openConfirmationDialog: boolean = false;
      let warnings: any;
      if (!payload.event.is_available) {
        let overlapData: any = [
          {
            'jobs': [],
            'timeoff': [],
            'non_job_events': [],
            'user_shifts': [],
            'routes': []
          }
        ];
        const nonJobOverlapPayload = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          from_date: eventStartTime, to_date: eventEndTime, user_uid: assignedTo.map((user: any) => user?.user_uid).join(',')
        };
        if (nonJobOverlapPayload.user_uid?.length) {
          const overlapResponse: any = await this.dataCollectorService.putData(this.dispatchBoardService.scheduleOverlapUrl, nonJobOverlapPayload).toPromise();
          overlapData = overlapResponse?.data || [];
        }
        const customUserDetails = this.dispatchBoardService.getCustomEmployeeDetailsRemote(overlapData);
        const eventStart: any = moment.tz(nonJobEventFormValue?.schedule?.eventDate?.clone().format('YYYY-MM-DD') + ' ' + nonJobEventFormValue?.schedule?.fromTimePicker?.clone().format('HH:mm'), 'YYYY-MM-DD HH:mm', timezone);
        const eventEnd: any = moment.tz(nonJobEventFormValue?.schedule?.eventDate?.clone().format('YYYY-MM-DD') + ' ' + nonJobEventFormValue?.schedule?.toTimePicker?.clone().format('HH:mm'), 'YYYY-MM-DD HH:mm', timezone);
        let eventDetails: any;
        if (this.options.state === 'new') {
          eventDetails = _.cloneDeep(payload.event);
          eventDetails['assigned_to'] = nonJobEventFormValue?.assignedTo.map((assignee: any) => ({ user: assignee }));
        }
        else if (this.options.state === 'edit') {
          eventDetails = this.eventData;
          eventDetails['assigned_to'] = nonJobEventFormValue?.assignedTo.map((assignee: any) => ({ user: assignee }));
        }
        warnings = this.dispatchBoardService.getScheduleWarningMsg('nonJobEvent', eventDetails, {}, eventStart, eventEnd, customUserDetails, true, false, { timezone: payload.event.timezone });
        openConfirmationDialog = Object.values(warnings).filter((warning: any) => warning.show).length > 0;
      }
      if (openConfirmationDialog) {
        const warningMessage: string = this.dispatchBoardService.formatScheduleWarnings(warnings);
        const scheduleConfirmationDialog = this.zuperDialogService.open({
          title: this.utilityService.getLocaleValue('confirm.non_job.schedule.dialog.title', 'Confirm Non Job Schedule'),
          type: 'error',
          message: `<div class="space-y-2 mt-2">${warningMessage}</div>`,
          actions: {
            confirm: {
              show: true,
              label: 'Continue',
              color: 'primary',
              confirmDisplayKey: 'continue.text'
            },
            cancel: {
              show: true,
              label: 'Cancel',
              cancelDisplayKey: 'cancel.text'
            }
          },
          dismissible: false,
          extraClass: 'w-1/3'
        });
        scheduleConfirmationDialog.afterClosed().subscribe((status: boolean) => {
          if (status) {
            this.createNonJobEvent(payload, nonJobEventFormValue);
          }
        });
      }
      else {
        this.createNonJobEvent(payload, nonJobEventFormValue);
      }
    }
  }

  createNonJobEvent(payload: any, nonJobEventFormValue: any): void {
    /* eslint-enable @typescript-eslint/naming-convention */
    let nonJobEventUrl: string;
    if (this.options.state === 'new') {
      this.isLoading = true;
      const createToastRef = this.toastService.loading(this.utilityService.getLocaleValue('non_job_event.creating.toast'));
      nonJobEventUrl = `${this.dataCollectorService.baseurl()}/non_job_events`;
      this.dataCollectorService.postData(nonJobEventUrl, payload).subscribe((response: any) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.emitAction({ isClosed: true, isCompleted: true, action: 'create', isRecurrence: !!nonJobEventFormValue?.recurrence,  data: { ...response?.data || {}, ...payload }, options: this.options });
        if (response?.message) {
          this.toastService.success(response?.message);
        }
        createToastRef.close();
        if (payload.event?.['is_recurrence']) {
          this.dispatchBoardService.trackMixPanelEvent('recurring_non_job_event_created');
        }
        if (payload?.event?.all_day) {
          this.dispatchBoardService.trackMixPanelEvent('all_day_non_job_event_created');
        }
        this.dispatchBoardService.trackMixPanelEvent('non_job_event_created');
        this.isLoading = false;
      },
      (error: any) => {
        this.isLoading = false;
        createToastRef.close();
      });
    }
    else if (this.options.state === 'edit' && this.eventData?.event_uid) {
      if (this.eventData?.is_recurrence) {
        payload['update_recurrence_type'] = this.options?.editType || 'SINGLE_EVENT';
        const updateToastRef = this.toastService.loading(this.utilityService.getLocaleValue('non_job_event.updating.toast'));
        nonJobEventUrl = `${this.dataCollectorService.baseurl()}/non_job_events/${this.eventData?.event_uid}`;
        this.dataCollectorService.putData(nonJobEventUrl, payload).subscribe((updateResponse: any) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          this.emitAction({ isClosed: true, isRecurrence: payload?.['update_recurrence_type'] === 'ALL_FUTURE_EVENTS', action: 'edit', isCompleted: true, data: { ...updateResponse?.data || {}, ...payload, event_uid: this.eventData?.event_uid }, options: this.options });
          if (updateResponse?.message) {
            this.toastService.success(updateResponse?.message);
          }
          updateToastRef.close();
          this.isLoading = false;
        }, (error: any) => {
          this.isLoading = false;
          updateToastRef.close();
        });
      }
      else {
        this.isLoading = true;
        const updateToastRef = this.toastService.loading(this.utilityService.getLocaleValue('non_job_event.updating.toast'));
        nonJobEventUrl = `${this.dataCollectorService.baseurl()}/non_job_events/${this.eventData?.event_uid}`;
        this.dataCollectorService.putData(nonJobEventUrl, payload).subscribe((response: any) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          this.emitAction({ isClosed: true, isRecurrence: false, action: 'edit', isCompleted: true, data: { ...response?.data || {}, ...payload, event_uid: this.eventData?.event_uid }, options: this.options });
          if (response?.message) {
            this.toastService.success(response?.message);
          }
          updateToastRef.close();
          this.isLoading = false;
        }, (error: any) => {
          this.isLoading = false;
          updateToastRef.close();
        });
      }
    }
  }
  //Drawer controls
  drawerOpenedChanged(isOpened: any): void {
    if (isOpened) {
      this.initDependencies();
    }
    else {
      this.openDrawer = false;
      //Reset reactive form after a delay to execute ngOndestroy first in recurrence component
      setTimeout(() => { this.tearDown(); });
    }
  }

  closeDrawer(): void {
    this.openDrawer = false;
    const drawerRef = this.zuperDrawerService.getComponent(this.drawerName);
    if (drawerRef) {
      drawerRef.close();
      this.emitAction({ isClosed: true });
    }
  }
}
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function maxPlainTextLengthValidator(maxLength: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value || '';
    const plainText = value.replace(/<[^>]*>/g, '');
    return plainText.length > maxLength ? { 'maxPlainTextLength': true } : null;
  };
}
